import { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Input,
  Table,
  Select,
  InputNumber,
  Space,
  message,
  Spin,
  Tooltip,
  Collapse,
  Result,
} from "antd";
import { Mail, AlertTriangle, ArrowLeftRight, CheckCircle2 } from "lucide-react";
import dayjs from "dayjs";
import "dayjs/locale/es";
import usePassengers from "../hooks/usePassengers";
import useGroups from "../hooks/useGroups";
import useHotels from "../hooks/useHotels";
import useUsers from "../hooks/useUsers";
import useExcursions from "../hooks/useExcursions";
import { calculatePassengerTotalFromExcursion } from "../utils/passengerCalculations";

dayjs.locale("es");

const { TextArea } = Input;

function EmailParserModal({
  onSuccess,
  externalOpen = false,
  onClose: externalOnClose,
  initialContent = "",
  initialSubject = "",
  emailImportId = null,
  hideButton = false,
  initialParsedData = null,
  readOnly = false,
  initialPassengers = null, // For readOnly mode - pre-loaded passengers from API
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [step, setStep] = useState(1); // 1 = input email, 2 = review passengers
  const [emailContent, setEmailContent] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [parsedPassengers, setParsedPassengers] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [globalGroup, setGlobalGroup] = useState(null);
  const [globalSeller, setGlobalSeller] = useState(null);
  const [hierarchyMode, setHierarchyMode] = useState("hotel"); // "hotel" or "excursion"
  const [successModal, setSuccessModal] = useState({ open: false, count: 0, sellerName: "" });

  // Use external or internal open state
  const open = externalOpen || internalOpen;

  const { parseEmail, bulkCreatePassengers, loading } = usePassengers();
  const { getGroups, groups } = useGroups();
  const { getHotels, hotels } = useHotels();
  const { getUsers, users } = useUsers();
  const { getExcursions, excursions } = useExcursions();

  // Initialize with external content when opening externally
  useEffect(() => {
    if (externalOpen) {
      setEmailContent(initialContent || "");
      setEmailSubject(initialSubject || "");

      // For readOnly mode with already processed passengers from API
      if (readOnly && initialPassengers) {
        const passengers = initialPassengers.map((p, index) => ({
          key: index,
          name: p.name,
          hotel_id: p.hotel?.id || null,
          hotel_name: p.hotel?.name || "",
          scheduled_excursion_id: p.scheduledExcursion?.id || null,
          scheduled_excursion_name: p.scheduledExcursion?.excursionType?.name || "",
          scheduled_excursion_day: p.scheduledExcursion?.day || "",
          adults_count: p.adultsCount ?? 0,
          minors_count: p.minorsCount ?? 0,
          free_count: p.freeCount ?? 0,
          total_amount: p.totalAmount ?? 0,
          original_text: "",
          original_excursion_text: "",
          hotel_matched: true,
          excursion_matched: true,
        }));

        // Sort passengers by hotel name
        const sortedPassengers = [...passengers].sort((a, b) => {
          const hotelA = a.hotel_name || "";
          const hotelB = b.hotel_name || "";
          if (!hotelA && hotelB) return -1;
          if (hotelA && !hotelB) return 1;
          return hotelA.localeCompare(hotelB);
        });

        const reindexedPassengers = sortedPassengers.map((p, i) => ({ ...p, key: i }));
        setParsedPassengers(reindexedPassengers);

        // Set group and seller from first passenger
        if (initialPassengers[0]?.group?.id) {
          setGlobalGroup(initialPassengers[0].group.id);
        }
        if (initialPassengers[0]?.seller?.id) {
          setGlobalSeller(initialPassengers[0].seller.id);
        }

        setStep(2);
      }
      // If we have pre-parsed data from Celery, skip to step 2
      else if (initialParsedData) {
        const data = initialParsedData;
        const passengers = data.passengers?.map((p, index) => ({
          key: index,
          name: p.name,
          hotel_id: p.hotel?.id || null,
          hotel_name: p.hotel?.name || "",
          scheduled_excursion_id: p.scheduledExcursion?.id || p.scheduled_excursion?.id || null,
          scheduled_excursion_name: p.scheduledExcursion?.name || p.scheduled_excursion?.name || "",
          scheduled_excursion_day: p.scheduledExcursion?.day || p.scheduled_excursion?.day || "",
          adults_count: p.adultsCount ?? p.adults_count ?? 0,
          minors_count: p.minorsCount ?? p.minors_count ?? 0,
          free_count: p.freeCount ?? p.free_count ?? 0,
          total_amount: p.totalAmount ?? p.total_amount ?? 0,
          original_text: p.originalText || p.original_text || "",
          original_excursion_text: p.originalExcursionText || p.original_excursion_text || "",
          hotel_matched: p.hotel?.matched || false,
          excursion_matched: p.scheduledExcursion?.matched || p.scheduled_excursion?.matched || false,
        })) || [];

        // Sort passengers: without hotel first, then by hotel name
        const sortedPassengers = [...passengers].sort((a, b) => {
          const hotelA = a.hotel_name || "";
          const hotelB = b.hotel_name || "";
          if (!hotelA && hotelB) return -1;
          if (hotelA && !hotelB) return 1;
          return hotelA.localeCompare(hotelB);
        });

        const reindexedPassengers = sortedPassengers.map((p, i) => ({ ...p, key: i }));
        setParsedPassengers(reindexedPassengers);

        // Set global group and seller if matched
        if (data.group?.matched) {
          setGlobalGroup(data.group.id);
        }
        if (data.seller?.matched) {
          setGlobalSeller(data.seller.id);
        }

        setStep(2); // Skip directly to review step
      }
    }
  }, [externalOpen, initialContent, initialSubject, initialParsedData, readOnly, initialPassengers]);

  useEffect(() => {
    if (open) {
      getGroups({
        check_in_date_range_before: dayjs().format("YYYY-MM-DD"),
        check_out_date_range_after: dayjs().format("YYYY-MM-DD"),
      });
      getHotels();
      getUsers();
      getExcursions({ status: "ACTIVE" });
    }
  }, [open]);

  const handleOpen = () => {
    setInternalOpen(true);
    setStep(1);
    setEmailContent("");
    setEmailSubject("");
    setParsedPassengers([]);
    setWarnings([]);
    setGlobalGroup(null);
    setGlobalSeller(null);
  };

  const handleClose = () => {
    setInternalOpen(false);
    setStep(1);
    setEmailContent("");
    setEmailSubject("");
    setParsedPassengers([]);
    setWarnings([]);
    setGlobalGroup(null);
    setGlobalSeller(null);
    if (externalOnClose) {
      externalOnClose();
    }
  };

  const handleParseEmail = async () => {
    if (!emailContent.trim()) {
      message.warning("Por favor pegue el contenido del email");
      return;
    }

    const result = await parseEmail(emailContent);

    if (result && result.success) {
      // Backend returns camelCase due to djangorestframework-camel-case
      const data = result.parsedData || result.parsed_data;
      const passengers = data.passengers.map((p, index) => ({
        key: index,
        name: p.name,
        hotel_id: p.hotel?.id || null,
        hotel_name: p.hotel?.name || "",
        scheduled_excursion_id: p.scheduledExcursion?.id || p.scheduled_excursion?.id || null,
        scheduled_excursion_name: p.scheduledExcursion?.name || p.scheduled_excursion?.name || "",
        scheduled_excursion_day: p.scheduledExcursion?.day || p.scheduled_excursion?.day || "",
        adults_count: p.adultsCount ?? p.adults_count ?? 0,
        minors_count: p.minorsCount ?? p.minors_count ?? 0,
        free_count: p.freeCount ?? p.free_count ?? 0,
        total_amount: p.totalAmount ?? p.total_amount ?? 0,
        original_text: p.originalText || p.original_text || "",
        original_excursion_text: p.originalExcursionText || p.original_excursion_text || "",
        hotel_matched: p.hotel?.matched || false,
        excursion_matched: p.scheduledExcursion?.matched || p.scheduled_excursion?.matched || false,
      }));

      // Sort passengers: without hotel first, then by hotel name
      const sortedPassengers = [...passengers].sort((a, b) => {
        const hotelA = a.hotel_name || "";
        const hotelB = b.hotel_name || "";
        if (!hotelA && hotelB) return -1;
        if (hotelA && !hotelB) return 1;
        return hotelA.localeCompare(hotelB);
      });

      // Re-assign keys after sorting
      const reindexedPassengers = sortedPassengers.map((p, i) => ({ ...p, key: i }));

      setParsedPassengers(reindexedPassengers);
      setWarnings(result.warnings || []);

      // Set global group and seller if matched
      if (data.group?.matched) {
        setGlobalGroup(data.group.id);
      }
      if (data.seller?.matched) {
        setGlobalSeller(data.seller.id);
      }

      setStep(2);
    } else {
      message.error(result?.error || "Error al procesar el email");
    }
  };

  const handlePassengerChange = (index, field, value) => {
    const updated = [...parsedPassengers];
    updated[index][field] = value;
    setParsedPassengers(updated);
  };

  const handleRemovePassenger = (index) => {
    const updated = parsedPassengers.filter((_, i) => i !== index);
    setParsedPassengers(updated.map((p, i) => ({ ...p, key: i })));
  };

  // Group passengers by hotel and then by excursion (default)
  const groupPassengersByHotelAndExcursion = () => {
    const grouped = {};

    parsedPassengers.forEach((p) => {
      const hotelKey = p.hotel_name || "Sin hotel";
      const hotelId = p.hotel_id;
      const excursionKey = p.scheduled_excursion_name
        ? `${p.scheduled_excursion_name} (${p.scheduled_excursion_day})`
        : "Sin excursion";
      const excursionId = p.scheduled_excursion_id;

      if (!grouped[hotelKey]) {
        grouped[hotelKey] = {
          hotelId,
          excursions: {},
        };
      }
      if (!grouped[hotelKey].excursions[excursionKey]) {
        grouped[hotelKey].excursions[excursionKey] = {
          excursionId,
          passengers: [],
        };
      }
      grouped[hotelKey].excursions[excursionKey].passengers.push(p);
    });

    return grouped;
  };

  // Group passengers by excursion and then by hotel (alternative)
  const groupPassengersByExcursionAndHotel = () => {
    const grouped = {};

    parsedPassengers.forEach((p) => {
      const excursionKey = p.scheduled_excursion_name
        ? `${p.scheduled_excursion_name} (${p.scheduled_excursion_day})`
        : "Sin excursion";
      const excursionId = p.scheduled_excursion_id;
      const hotelKey = p.hotel_name || "Sin hotel";
      const hotelId = p.hotel_id;

      if (!grouped[excursionKey]) {
        grouped[excursionKey] = {
          excursionId,
          hotels: {},
        };
      }
      if (!grouped[excursionKey].hotels[hotelKey]) {
        grouped[excursionKey].hotels[hotelKey] = {
          hotelId,
          passengers: [],
        };
      }
      grouped[excursionKey].hotels[hotelKey].passengers.push(p);
    });

    return grouped;
  };

  // Update hotel for all passengers in a hotel group
  // parentKey is the excursion key when in excursion-first mode, null when in hotel-first mode (top level)
  const handleHotelGroupChange = (oldHotelName, newHotelId, parentKey = null) => {
    const hotel = hotels.results?.find((h) => h.id === newHotelId);
    const updated = parsedPassengers.map((p) => {
      const pHotelName = p.hotel_name || "Sin hotel";
      const pExcursionKey = p.scheduled_excursion_name
        ? `${p.scheduled_excursion_name} (${p.scheduled_excursion_day})`
        : "Sin excursion";

      // If parentKey is provided (excursion-first mode), match both excursion and hotel
      // Otherwise (hotel-first mode), just match hotel
      const shouldUpdate = parentKey
        ? (pExcursionKey === parentKey && pHotelName === oldHotelName)
        : (pHotelName === oldHotelName);

      if (shouldUpdate) {
        return {
          ...p,
          hotel_id: newHotelId,
          hotel_name: hotel?.name || "",
          hotel_matched: true,
        };
      }
      return p;
    });
    setParsedPassengers(updated);
  };

  // Update excursion for all passengers in an excursion group
  // parentKey is the hotel key when in hotel-first mode, null when in excursion-first mode (top level)
  const handleExcursionGroupChange = (oldExcursionKey, newExcursionId, parentKey = null) => {
    const excursion = excursions.results?.find((e) => e.id === newExcursionId);
    const updated = parsedPassengers.map((p) => {
      const pExcursionKey = p.scheduled_excursion_name
        ? `${p.scheduled_excursion_name} (${p.scheduled_excursion_day})`
        : "Sin excursion";
      const pHotelName = p.hotel_name || "Sin hotel";

      // If parentKey is provided (hotel-first mode), match both hotel and excursion
      // Otherwise (excursion-first mode), just match excursion
      const shouldUpdate = parentKey
        ? (pHotelName === parentKey && pExcursionKey === oldExcursionKey)
        : (pExcursionKey === oldExcursionKey);

      if (shouldUpdate) {
        return {
          ...p,
          scheduled_excursion_id: newExcursionId,
          scheduled_excursion_name: excursion?.excursionType?.name || "",
          scheduled_excursion_day: excursion?.day || "",
          excursion_matched: true,
        };
      }
      return p;
    });
    setParsedPassengers(updated);
  };

  const handleSaveAll = async () => {
    // Validate required fields
    if (!globalGroup) {
      message.error("Debe seleccionar un grupo");
      return;
    }
    if (!globalSeller) {
      message.error("Debe seleccionar un vendedor");
      return;
    }

    const invalidPassengers = parsedPassengers.filter(
      (p) => !p.hotel_id || !p.scheduled_excursion_id
    );

    if (invalidPassengers.length > 0) {
      message.error(
        "Todos los pasajeros deben tener hotel y excursión seleccionados"
      );
      return;
    }

    // Format passengers for bulk create
    // totalAmount should be the calculated price (adults * priceAdult + minors * priceMinor)
    // payments should be the actual amount paid (from email)
    const formattedPassengers = parsedPassengers.map((p) => {
      // Find the excursion to get prices
      const excursion = excursions.results?.find((e) => e.id === p.scheduled_excursion_id);

      // Calculate theoretical total based on quantities and excursion prices
      const calculatedTotal = calculatePassengerTotalFromExcursion({
        adultsCount: p.adults_count || 0,
        minorsCount: p.minors_count || 0,
        excursion,
      });

      // The email amount is what was actually paid
      const paidAmount = p.total_amount || 0;

      return {
        name: p.name,
        group: globalGroup,
        hotel: p.hotel_id,
        seller: globalSeller,
        scheduledExcursion: p.scheduled_excursion_id,
        adultsCount: p.adults_count,
        minorsCount: p.minors_count,
        babyCount: 0,
        freeCount: p.free_count,
        totalAmount: calculatedTotal, // Calculated price, not email amount
        payments: paidAmount > 0 ? [
          {
            user: globalSeller,
            amount: paidAmount, // Actual payment from email
            paymentMethod: null,
          },
        ] : [],
      };
    });

    // Include email_import data for persistence
    const payload = {
      passengers: formattedPassengers,
      emailImport: emailImportId
        ? { id: emailImportId }  // Link to existing EmailImport
        : {
            subject: emailSubject,
            content: emailContent,
          },
    };

    const result = await bulkCreatePassengers(payload);

    if (result && Array.isArray(result)) {
      // Get seller name for the success modal
      const selectedSeller = users?.find((u) => u.id === globalSeller);
      const sellerName = selectedSeller
        ? `${selectedSeller.firstName} ${selectedSeller.lastName}`
        : "el vendedor";

      setSuccessModal({
        open: true,
        count: parsedPassengers.length,
        sellerName,
      });
    }
  };

  // Columnas para tabla dentro de cada grupo de hotel (sin hotel ni excursion, se infiere de jerarquía)
  const getPassengerColumns = () => {
    const columns = [
      {
        title: "Nombre",
        dataIndex: "name",
        key: "name",
        render: (text, record) => (
          <Input
            value={text}
            onChange={(e) => handlePassengerChange(record.key, "name", e.target.value)}
            size="small"
            placeholder="Nombre"
            disabled={readOnly}
          />
        ),
      },
      {
        title: "Adultos",
        dataIndex: "adults_count",
        key: "adults_count",
        width: 70,
        render: (value, record) => (
          <InputNumber
            value={value}
            onChange={(val) => handlePassengerChange(record.key, "adults_count", val)}
            size="small"
            min={0}
            className="w-full"
            disabled={readOnly}
          />
        ),
      },
      {
        title: "Menores",
        dataIndex: "minors_count",
        key: "minors_count",
        width: 70,
        render: (value, record) => (
          <InputNumber
            value={value}
            onChange={(val) => handlePassengerChange(record.key, "minors_count", val)}
            size="small"
            min={0}
            className="w-full"
            disabled={readOnly}
          />
        ),
      },
      {
        title: "Lib.",
        dataIndex: "free_count",
        key: "free_count",
        width: 60,
        render: (value, record) => (
          <InputNumber
            value={value}
            onChange={(val) => handlePassengerChange(record.key, "free_count", val)}
            size="small"
            min={0}
            className="w-full"
            disabled={readOnly}
          />
        ),
      },
      {
        title: "Monto",
        dataIndex: "total_amount",
        key: "total_amount",
        width: 100,
        render: (value, record) => (
          <InputNumber
            value={value}
            onChange={(val) => handlePassengerChange(record.key, "total_amount", val)}
            size="small"
            min={0}
            className="w-full"
            formatter={(v) => `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            parser={(v) => v.replace(/\$\s?|(,*)/g, "")}
            disabled={readOnly}
          />
        ),
      },
    ];

    // Only add actions column if not readOnly
    if (!readOnly) {
      columns.push({
        title: "",
        key: "actions",
        width: 40,
        render: (_, record) => (
          <Button
            type="text"
            danger
            size="small"
            onClick={() => handleRemovePassenger(record.key)}
          >
            X
          </Button>
        ),
      });
    }

    return columns;
  };

  // Render hierarchical view based on current mode
  const renderHierarchicalView = () => {
    if (hierarchyMode === "hotel") {
      // Hotel > Excursion > Passengers (default)
      const grouped = groupPassengersByHotelAndExcursion();
      const hotelKeys = Object.keys(grouped);

      // Sort: "Sin hotel" first, then alphabetically
      hotelKeys.sort((a, b) => {
        if (a === "Sin hotel") return -1;
        if (b === "Sin hotel") return 1;
        return a.localeCompare(b);
      });

      return (
        <div className="flex-1 overflow-y-auto">
          <Collapse accordion defaultActiveKey={hotelKeys[0]}>
            {hotelKeys.map((hotelKey) => {
              const hotelData = grouped[hotelKey];
              const excursionKeys = Object.keys(hotelData.excursions);
              const totalPassengersInHotel = excursionKeys.reduce(
                (sum, ek) => sum + hotelData.excursions[ek].passengers.length,
                0
              );

              // Sort excursions by date (extract date from key like "City (2025-11-23)")
              excursionKeys.sort((a, b) => {
                if (a === "Sin excursion") return -1;
                if (b === "Sin excursion") return 1;
                const dateA = a.match(/\((\d{4}-\d{2}-\d{2})\)/)?.[1] || "";
                const dateB = b.match(/\((\d{4}-\d{2}-\d{2})\)/)?.[1] || "";
                return dateA.localeCompare(dateB);
              });

              return (
                <Collapse.Panel
                  key={hotelKey}
                  header={
                    <div className="flex justify-between items-center w-full pr-4">
                      <Select
                        size="small"
                        value={hotelData.hotelId}
                        onChange={(val) => handleHotelGroupChange(hotelKey, val)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-64"
                        showSearch
                        optionFilterProp="label"
                        placeholder="Seleccionar hotel"
                        status={!hotelData.hotelId ? "warning" : undefined}
                        disabled={readOnly}
                        options={hotels.results?.map((h) => ({
                          value: h.id,
                          label: h.name,
                        }))}
                      />
                      <span className="font-bold text-gray-500">
                        "{hotelKey}" ({totalPassengersInHotel} pasajeros)
                      </span>
                    </div>
                  }
                >
                  {excursionKeys.map((excursionKey) => {
                    const excursionData = hotelData.excursions[excursionKey];
                    // Get original excursion text from first passenger (e.g., "Sábado Miramar")
                    const firstPassenger = excursionData.passengers[0];
                    const originalExcursionText = excursionKey === "Sin excursion"
                      ? "Sin excursión"
                      : firstPassenger?.original_excursion_text || excursionKey;
                    return (
                      <div key={excursionKey} className="mb-4">
                        <div className="flex justify-between items-center mb-2 bg-gray-100 p-2 rounded">
                          <Select
                            size="small"
                            value={excursionData.excursionId}
                            onChange={(val) =>
                              handleExcursionGroupChange(excursionKey, val, hotelKey)
                            }
                            className="w-72"
                            showSearch
                            optionFilterProp="label"
                            placeholder="Seleccionar excursión"
                            status={!excursionData.excursionId ? "warning" : undefined}
                            disabled={readOnly}
                            options={excursions.results?.map((e) => ({
                              value: e.id,
                              label: `${e.excursionType.name} (${dayjs(e.day).format("ddd DD/MM")})`,
                            }))}
                          />
                          <span className="font-medium text-gray-500 italic">
                            "{originalExcursionText}" ({excursionData.passengers.length})
                          </span>
                        </div>
                        <Table
                          size="small"
                          columns={getPassengerColumns()}
                          dataSource={excursionData.passengers}
                          pagination={false}
                          rowKey="key"
                        />
                      </div>
                    );
                  })}
                </Collapse.Panel>
              );
            })}
          </Collapse>
        </div>
      );
    } else {
      // Excursion > Hotel > Passengers (alternative)
      const grouped = groupPassengersByExcursionAndHotel();
      const excursionKeys = Object.keys(grouped);

      // Sort excursions by date (extract date from key like "City (2025-11-23)")
      excursionKeys.sort((a, b) => {
        if (a === "Sin excursion") return -1;
        if (b === "Sin excursion") return 1;
        const dateA = a.match(/\((\d{4}-\d{2}-\d{2})\)/)?.[1] || "";
        const dateB = b.match(/\((\d{4}-\d{2}-\d{2})\)/)?.[1] || "";
        return dateA.localeCompare(dateB);
      });

      return (
        <div className="flex-1 overflow-y-auto">
          <Collapse accordion defaultActiveKey={excursionKeys[0]}>
            {excursionKeys.map((excursionKey) => {
              const excursionData = grouped[excursionKey];
              const hotelKeys = Object.keys(excursionData.hotels);
              const totalPassengersInExcursion = hotelKeys.reduce(
                (sum, hk) => sum + excursionData.hotels[hk].passengers.length,
                0
              );

              // Sort hotels
              hotelKeys.sort((a, b) => {
                if (a === "Sin hotel") return -1;
                if (b === "Sin hotel") return 1;
                return a.localeCompare(b);
              });

              // Get original excursion text from first passenger (e.g., "Sábado Miramar")
              const firstPassengerInExc = excursionData.hotels[hotelKeys[0]]?.passengers[0];
              const originalExcTextPanel = excursionKey === "Sin excursion"
                ? "Sin excursión"
                : firstPassengerInExc?.original_excursion_text || excursionKey;

              return (
                <Collapse.Panel
                  key={excursionKey}
                  header={
                    <div className="flex justify-between items-center w-full pr-4">
                      <Select
                        size="small"
                        value={excursionData.excursionId}
                        onChange={(val) => handleExcursionGroupChange(excursionKey, val)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-72"
                        showSearch
                        optionFilterProp="label"
                        placeholder="Seleccionar excursión"
                        status={!excursionData.excursionId ? "warning" : undefined}
                        disabled={readOnly}
                        options={excursions.results?.map((e) => ({
                          value: e.id,
                          label: `${e.excursionType.name} (${dayjs(e.day).format("ddd DD/MM")})`,
                        }))}
                      />
                      <span className="font-bold text-blue-700">
                        "{originalExcTextPanel}" ({totalPassengersInExcursion} pasajeros)
                      </span>
                    </div>
                  }
                >
                  {hotelKeys.map((hotelKey) => {
                    const hotelData = excursionData.hotels[hotelKey];
                    return (
                      <div key={hotelKey} className="mb-4">
                        <div className="flex justify-between items-center mb-2 bg-gray-100 p-2 rounded">
                          <Select
                            size="small"
                            value={hotelData.hotelId}
                            onChange={(val) =>
                              handleHotelGroupChange(hotelKey, val, excursionKey)
                            }
                            className="w-64"
                            showSearch
                            optionFilterProp="label"
                            placeholder="Seleccionar hotel"
                            status={!hotelData.hotelId ? "warning" : undefined}
                            disabled={readOnly}
                            options={hotels.results?.map((h) => ({
                              value: h.id,
                              label: h.name,
                            }))}
                          />
                          <span className="font-medium text-gray-500 italic">
                            "{hotelKey}" ({hotelData.passengers.length})
                          </span>
                        </div>
                        <Table
                          size="small"
                          columns={getPassengerColumns()}
                          dataSource={hotelData.passengers}
                          pagination={false}
                          rowKey="key"
                        />
                      </div>
                    );
                  })}
                </Collapse.Panel>
              );
            })}
          </Collapse>
        </div>
      );
    }
  };

  return (
    <>
      {!hideButton && (
        <Button icon={<Mail size={16} />} onClick={handleOpen}>
          Cargar desde Email
        </Button>
      )}

      <Modal
        title={
          readOnly
            ? "Detalle de Importación"
            : step === 1
            ? "Pegar contenido del email"
            : "Revisar pasajeros parseados"
        }
        open={open}
        onCancel={handleClose}
        width={step === 1 ? 600 : "95vw"}
        style={step === 2 ? { top: 20, maxWidth: 1800 } : undefined}
        footer={
          readOnly ? (
            <Button onClick={handleClose}>Cerrar</Button>
          ) : step === 1 ? (
            <Space>
              <Button onClick={handleClose}>Cancelar</Button>
              <Button
                type="primary"
                onClick={handleParseEmail}
                loading={loading.post}
              >
                Procesar Email
              </Button>
            </Space>
          ) : (
            <Space>
              <Button onClick={() => setStep(1)}>Volver</Button>
              <Button onClick={handleClose}>Cancelar</Button>
              <Button
                type="primary"
                onClick={handleSaveAll}
                loading={loading.post}
                disabled={parsedPassengers.length === 0}
              >
                Guardar {parsedPassengers.length} Pasajeros
              </Button>
            </Space>
          )
        }
      >
        {step === 1 ? (
          <div>
            <p className="mb-2 text-gray-600">
              Pegue el contenido del email con la lista de pasajeros. El sistema
              usara IA para extraer la informacion.
            </p>
            <Input
              placeholder="Asunto del email (opcional)"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              className="mb-3"
            />
            <TextArea
              rows={14}
              value={emailContent}
              onChange={(e) => setEmailContent(e.target.value)}
              placeholder={`Ejemplo:
De: Juan Perez
Hotel Costa Mar
Sabado Miramar:
Garcia x2 15000
Rodriguez x3+1 25000
Lopez x1+1L 8000`}
            />
          </div>
        ) : (
          <Spin spinning={loading.post}>
            <div className="flex gap-4 h-[75vh]">
              {/* Columna izquierda: Email original */}
              <div className="w-[30%] border rounded p-3 bg-gray-50 flex flex-col">
                <div className="font-medium mb-2 text-gray-600">Email Original</div>
                <pre className="whitespace-pre-wrap text-xs flex-1 overflow-y-auto font-mono">
                  {emailContent}
                </pre>
              </div>

              {/* Columna derecha: Datos parseados */}
              <div className="w-[70%] flex flex-col overflow-hidden">
                {warnings.length > 0 && (
                  <Collapse
                    size="small"
                    className="mb-4 flex-shrink-0"
                    items={[
                      {
                        key: "warnings",
                        label: (
                          <span className="flex items-center gap-2 text-orange-600">
                            <AlertTriangle size={14} />
                            {warnings.length} advertencia{warnings.length > 1 ? "s" : ""} del parseo
                          </span>
                        ),
                        children: (
                          <ul className="list-disc pl-4 text-sm text-gray-600">
                            {warnings.map((w, i) => (
                              <li key={i}>{w}</li>
                            ))}
                          </ul>
                        ),
                      },
                    ]}
                  />
                )}

                <div className="flex gap-4 mb-4 flex-shrink-0">
                  <div className="flex-1">
                    <label className="block mb-1 font-medium">
                      Grupo (para todos)
                    </label>
                    <Select
                      value={globalGroup}
                      onChange={setGlobalGroup}
                      className="w-full"
                      showSearch
                      optionFilterProp="label"
                      placeholder="Seleccionar grupo"
                      status={!globalGroup ? "error" : undefined}
                      disabled={readOnly}
                      options={groups.results?.map((g) => ({
                        value: g.id,
                        label: `${g.selectorDisplay} (${dayjs(g.checkInDate).format(
                          "DD/MM"
                        )})`,
                      }))}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block mb-1 font-medium">
                      Vendedor (para todos)
                    </label>
                    <Select
                      value={globalSeller}
                      onChange={setGlobalSeller}
                      className="w-full"
                      showSearch
                      optionFilterProp="label"
                      placeholder="Seleccionar vendedor"
                      status={!globalSeller ? "error" : undefined}
                      disabled={readOnly}
                      options={users?.map((u) => ({
                        value: u.id,
                        label: `${u.firstName} ${u.lastName}`,
                      }))}
                    />
                  </div>
                </div>

                <div className="flex justify-between items-center mb-2 flex-shrink-0">
                  <span className="text-sm text-gray-500">
                    Agrupado por: {hierarchyMode === "hotel" ? "Hotel → Excursión" : "Excursión → Hotel"}
                  </span>
                  {!readOnly && (
                    <Button
                      size="small"
                      icon={<ArrowLeftRight size={14} />}
                      onClick={() => setHierarchyMode(hierarchyMode === "hotel" ? "excursion" : "hotel")}
                    >
                      Cambiar agrupación
                    </Button>
                  )}
                </div>

                {renderHierarchicalView()}

                <div className="mt-2 text-right text-gray-500 flex-shrink-0">
                  Total pasajeros: {parsedPassengers.length}
                </div>
              </div>
            </div>
          </Spin>
        )}
      </Modal>

      {/* Success Modal */}
      <Modal
        open={successModal.open}
        footer={null}
        onCancel={() => {
          setSuccessModal({ open: false, count: 0, sellerName: "" });
          handleClose();
          onSuccess && onSuccess();
        }}
        centered
        width={420}
        closable={false}
      >
        <Result
          icon={
            <div className="flex flex-col items-center gap-3">
              <CheckCircle2 size={56} className="text-green-500" />
              <Mail size={36} className="text-blue-500" />
            </div>
          }
          title={`¡${successModal.count} pasajeros guardados!`}
          subTitle={`Se envió un mail automático a ${successModal.sellerName} con la rendición de este grupo.`}
          extra={
            <Button
              type="primary"
              size="large"
              onClick={() => {
                setSuccessModal({ open: false, count: 0, sellerName: "" });
                handleClose();
                onSuccess && onSuccess();
              }}
            >
              Aceptar
            </Button>
          }
        />
      </Modal>
    </>
  );
}

export default EmailParserModal;
